import { getAdpSessionNode } from "@/features/adp/hooks/useAdpSessionStore";

export type TargetColumn = {
  column_name: string;
  column_data_type: string;
};

export type DataFlowMappingRequestPayload = {
  roo_id: number[];
  source_file_id: number[];
  source_data_source_id: number[];
  target_data_source_id: number[];
  target_table_id: number[];
};

export type MappingPayloadField = keyof DataFlowMappingRequestPayload;

export type ColumnMappingRow = {
  sourceName: string;
  sourceColumn: string;
  sourceDataType: string;
  destinationName: string;
  destinationDataType: string;
};

export type RenameOverride = {
  oldColumn: string;
  newColumn: string;
};

export type AggregationReplayProjection = {
  dropFields: string[];
  derivedColumns: Array<{ columnName: string; dataType: string }>;
};



export function normalizeText(value: string): string {
  return value.trim().toLowerCase();
}

export function isTargetColumn(value: unknown): value is TargetColumn {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.column_name === "string"
    && typeof candidate.column_data_type === "string"
  );
}

function resolveTargetColumnMap(targetColumns: TargetColumn[]): Map<string, TargetColumn> {
  const map = new Map<string, TargetColumn>();
  for (const targetColumn of targetColumns) {
    map.set(normalizeText(targetColumn.column_name), targetColumn);
  }
  return map;
}

type SourceFieldRowForMapping = {
  field_name: string;
  field_data_type: string;
};

function isFlatSourceFieldRow(item: unknown): item is SourceFieldRowForMapping {
  if (!item || typeof item !== "object") return false;
  const o = item as Record<string, unknown>;
  return typeof o.field_name === "string" && typeof o.field_data_type === "string";
}

/**
 * `source_files` in session may be either:
 * - `SourceFile`-like entries with nested `source_file_fields`, or
 * - a flat list of field rows (`field_name`, `field_data_type` on each item).
 */
function expandSourceFilesListItem(item: unknown): SourceFieldRowForMapping[] {
  if (isFlatSourceFieldRow(item)) {
    return [item];
  }
  if (item && typeof item === "object") {
    const nested = (item as Record<string, unknown>).source_file_fields;
    if (Array.isArray(nested)) {
      return nested.filter(isFlatSourceFieldRow);
    }
  }
  return [];
}

export function flattenSourceFilesToFieldRows(sourceFiles: unknown[]): SourceFieldRowForMapping[] {
  return sourceFiles.flatMap(expandSourceFilesListItem);
}

/** One source node’s files + display name (session may hold parent + child sources). */
export type SourceSessionModel = {
  sourceName: string;
  /** Raw `source_files` array from session (nested file shape or flat field rows). */
  sourceFiles: unknown[];
};

/**
 * Walks upstream **integration** nodes and returns a map keyed by each Join's
 * `joinTableDataSource` (the join-secondary source's `source_name`) to the set
 * of columns named in that Join's `joinTableColumns`.
 *
 * Used by `normalizeSourceSessionsForColumnMapping` to prune join-secondary
 * sources down to only their join-output columns — otherwise the secondary's
 * full source_files (incl. the join key already present on the primary) would
 * duplicate rows in the column-mapping payload.
 */
export function buildJoinSecondaryColumnAllowlist(
  upstreamIntegrationNodeIds: string[],
): Map<string, Set<string>> {
  const allowlist = new Map<string, Set<string>>();

  for (const nodeId of upstreamIntegrationNodeIds) {
    const record = getAdpSessionNode(nodeId);
    if (!record) continue;
    const entry = (record as { integrationData?: { data?: { abbreviation?: string; data?: unknown[] } } })
      .integrationData;
    if (entry?.data?.abbreviation !== "JR") continue;
    const items = Array.isArray(entry.data.data) ? entry.data.data : [];

    for (const item of items) {
      if (!item || typeof item !== "object") continue;
      const formData = (item as { formData?: Record<string, unknown> }).formData;
      if (!formData) continue;
      const joinTableDataSource =
        typeof formData.joinTableDataSource === "string" ? formData.joinTableDataSource : "";
      const joinTableColumns =
        typeof formData.joinTableColumns === "string" ? formData.joinTableColumns : "";
      if (!joinTableDataSource || !joinTableColumns.trim()) continue;
      const names = joinTableColumns
        .split(",")
        .map((n) => n.trim())
        .filter((n) => n.length > 0);
      if (names.length === 0) continue;
      const bucket = allowlist.get(joinTableDataSource) ?? new Set<string>();
      for (const n of names) bucket.add(n);
      allowlist.set(joinTableDataSource, bucket);
    }
  }

  return allowlist;
}

/**
 * Walks upstream **integration** nodes and returns the set of `source_name`s
 * that participate as a Union secondary (i.e. `withTableDataSource` in any
 * UN row). Used to fully drop those sources' rows from the column-mapping
 * table — for Union the secondary contributes logically the same columns as
 * the primary, so showing them again would duplicate every primary row.
 */
export function buildUnionSecondarySourceNames(
  upstreamIntegrationNodeIds: string[],
): Set<string> {
  const names = new Set<string>();

  for (const nodeId of upstreamIntegrationNodeIds) {
    const record = getAdpSessionNode(nodeId);
    if (!record) continue;
    const entry = (record as { integrationData?: { data?: { abbreviation?: string; data?: unknown[] } } })
      .integrationData;
    if (entry?.data?.abbreviation !== "UN") continue;
    const items = Array.isArray(entry.data.data) ? entry.data.data : [];

    for (const item of items) {
      if (!item || typeof item !== "object") continue;
      const formData = (item as { formData?: Record<string, unknown> }).formData;
      if (!formData) continue;
      const dataSource =
        typeof formData.withTableDataSource === "string"
          ? formData.withTableDataSource
          : "";
      if (dataSource) names.add(dataSource);
    }
  }

  return names;
}

export function normalizeSourceSessionsForColumnMapping(
  entries: Array<{ data: Record<string, unknown> }>,
  joinSecondaryAllowlist?: Map<string, Set<string>>,
  unionSecondarySources?: Set<string>,
): SourceSessionModel[] {
  return entries.map((entry) => {
    const data = entry.data;
    let rawFiles: unknown[] = [];
    if (Array.isArray(data.source_files)) {
      rawFiles = data.source_files;
    } else if (Array.isArray(data.sourceFiles)) {
      rawFiles = data.sourceFiles;
    }
    const sourceName = typeof data.source_name === "string" ? data.source_name : "";

    // Union secondary: drop all rows. The Union output has primary's schema,
    // so any column-mapping row from the secondary would duplicate a primary row.
    if (sourceName && unionSecondarySources?.has(sourceName)) {
      return { sourceName, sourceFiles: [] };
    }

    // Join secondary: keep only the join-output columns (allowlist filters).
    const allowedColumns = sourceName ? joinSecondaryAllowlist?.get(sourceName) : undefined;
    const effectiveFiles = allowedColumns
      ? rawFiles.filter((item) => {
          if (!item || typeof item !== "object") return false;
          const o = item as Record<string, unknown>;
          return typeof o.field_name === "string" && allowedColumns.has(o.field_name);
        })
      : rawFiles;
    return {
      sourceName,
      sourceFiles: effectiveFiles,
    };
  });
}

export function computeSourceFieldsSignature(models: SourceSessionModel[]): string {
  return models
    .flatMap(({ sourceFiles }) =>
      flattenSourceFilesToFieldRows(sourceFiles).map(
        (field) => `${field.field_name}:${field.field_data_type}`,
      ),
    )
    .join("|");
}

export function buildInitialColumnMappingRows(params: {
  sources: SourceSessionModel[];
  targetColumns: TargetColumn[];
  renameOverrides?: RenameOverride[];
}): ColumnMappingRow[] {
  const { sources, targetColumns, renameOverrides = [] } = params;
  const targetColumnMap = resolveTargetColumnMap(targetColumns);
  const renameMap = new Map<string, string>();
  for (const override of renameOverrides) {
    renameMap.set(normalizeText(override.oldColumn), override.newColumn);
  }

  const sourceRows = sources.flatMap(({ sourceName, sourceFiles }) =>
    flattenSourceFilesToFieldRows(sourceFiles).map((field) => {
      const sourceColumn = field.field_name;
      const sourceDataType = field.field_data_type;
      const renamedTargetCandidate = renameMap.get(normalizeText(sourceColumn)) ?? sourceColumn;
      const matchedTarget = targetColumnMap.get(normalizeText(renamedTargetCandidate));

      return {
        sourceName,
        sourceColumn,
        sourceDataType,
        destinationName: matchedTarget?.column_name ?? "",
        destinationDataType: matchedTarget?.column_data_type ?? "",
      };
    }),
  );

  const mappedDestinationNames = new Set(
    sourceRows
      .map((row) => normalizeText(row.destinationName))
      .filter((name) => name.length > 0),
  );
  const fallbackSourceName = sources.find((s) => s.sourceName.trim().length > 0)?.sourceName ?? "";
  const destinationOnlyRows = targetColumns
    .filter((targetColumn) => !mappedDestinationNames.has(normalizeText(targetColumn.column_name)))
    .map<ColumnMappingRow>((targetColumn) => ({
      sourceName: fallbackSourceName,
      sourceColumn: "",
      sourceDataType: "",
      destinationName: targetColumn.column_name,
      destinationDataType: targetColumn.column_data_type,
    }));

  return [...sourceRows, ...destinationOnlyRows];
}

export function buildColumnMappingRowsWithProjection(params: {
  sources: SourceSessionModel[];
  targetColumns: TargetColumn[];
  renameOverrides: RenameOverride[];
  rawFiles: unknown[];
  dropFields: string[];
  aggregationProjection: AggregationReplayProjection | null;
}): ColumnMappingRow[] {
  const { sources, targetColumns, renameOverrides, rawFiles, dropFields, aggregationProjection } = params;
  const dropSet = new Set(dropFields.map((c) => c.toLowerCase()));

  let rows = buildInitialColumnMappingRows({
    sources,
    targetColumns,
    renameOverrides,
  }).filter((row) => !dropSet.has(row.sourceColumn.toLowerCase()));

  if (!aggregationProjection) return rows;

  const projectedSourceNames = new Set(
    rawFiles
      .filter(isFlatSourceFieldRow)
      .map((f) => f.field_name.trim())
      .filter(Boolean)
      .filter((name) => !dropSet.has(name.toLowerCase()))
      .map((name) => name.toLowerCase()),
  );

  rows = rows.filter((row) => {
    const sourceName = row.sourceColumn.trim().toLowerCase();
    if (!sourceName) return true;
    return projectedSourceNames.has(sourceName);
  });
  return rows;
}

export function resolveDestinationDataType(
  destinationColumnName: string,
  targetColumns: TargetColumn[],
): string {
  const lookup = normalizeText(destinationColumnName);
  const matched = targetColumns.find(
    (column) => normalizeText(column.column_name) === lookup,
  );
  return matched?.column_data_type ?? "";
}

export function buildColumnMappingPayload(
  rows: ColumnMappingRow[],
): Array<[string | null, string | null, string, string]> {
  return rows.map((row) => [
    row.sourceColumn.trim() ? row.sourceColumn : null,
    row.sourceDataType.trim() ? row.sourceDataType : null,
    row.destinationName,
    row.destinationDataType,
  ]);
}

function asPositiveNumber(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null;
  return value;
}

function toUniquePositiveNumbers(values: unknown[]): number[] {
  const unique = new Set<number>();
  for (const value of values) {
    const normalized = asPositiveNumber(value);
    if (normalized != null) unique.add(normalized);
  }
  return [...unique];
}

function pickSourceFileIdsFromRawSourceFiles(sourceFiles: unknown[]): number[] {
  const directSourceFileIds = sourceFiles
    .map((row) =>
      row && typeof row === "object"
        ? asPositiveNumber((row as Record<string, unknown>).source_file_id)
        : null,
    )
    .filter((v): v is number => v != null);

  if (directSourceFileIds.length > 0) {
    return toUniquePositiveNumbers(directSourceFileIds);
  }

  const nestedFieldRows = sourceFiles.flatMap((item) =>
    item && typeof item === "object" && Array.isArray((item as Record<string, unknown>).source_file_fields)
      ? ((item as Record<string, unknown>).source_file_fields as unknown[])
      : [],
  );

  const nestedIds = nestedFieldRows
    .map((row) =>
      row && typeof row === "object"
        ? asPositiveNumber((row as Record<string, unknown>).source_file_id)
        : null,
    )
    .filter((v): v is number => v != null);

  return toUniquePositiveNumbers(nestedIds);
}

/**
 * Build `/data-flows/:id/mapping` payload from current source + destination session data.
 */
export function buildDataFlowMappingPayload(params: {
  sourceEntries: Array<{ data: Record<string, unknown> }>;
  targetData: Record<string, unknown> | null | undefined;
}): DataFlowMappingRequestPayload {
  const { sourceEntries, targetData } = params;

  const rooIds = toUniquePositiveNumbers(
    sourceEntries.map((entry) => entry.data.roo_id),
  );

  const sourceDataSourceIds = toUniquePositiveNumbers(
    sourceEntries.map((entry) => entry.data.source_data_source_id),
  );

  const sourceFileIds = toUniquePositiveNumbers(
    sourceEntries.flatMap((entry) => {
      const sourceFiles = Array.isArray(entry.data.source_files) ? entry.data.source_files : [];
      return pickSourceFileIdsFromRawSourceFiles(sourceFiles);
    }),
  );

  const targetDataSourceIds = toUniquePositiveNumbers([
    targetData?.target_data_source_id,
    ...(Array.isArray(targetData?.target_table)
      ? targetData.target_table.map((row) =>
        row && typeof row === "object"
          ? (row as Record<string, unknown>).target_data_source_id
          : null,
      )
      : []),
  ]);

  const targetTableIds = toUniquePositiveNumbers([
    targetData?.target_table_id,
    ...(Array.isArray(targetData?.target_table)
      ? targetData.target_table.map((row) =>
        row && typeof row === "object"
          ? (row as Record<string, unknown>).target_table_id
          : null,
      )
      : []),
  ]);

  return {
    roo_id: rooIds,
    source_file_id: sourceFileIds,
    source_data_source_id: sourceDataSourceIds,
    target_data_source_id: targetDataSourceIds,
    target_table_id: targetTableIds,
  };
}

export function getMissingMappingPayloadFields(
  payload: DataFlowMappingRequestPayload,
): MappingPayloadField[] {
  const requiredFields: MappingPayloadField[] = [
    "roo_id",
    "source_file_id",
    "source_data_source_id",
    "target_data_source_id",
    "target_table_id",
  ];
  return requiredFields.filter((field) => payload[field].length === 0);
}


